import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Box, Typography, Chip, Tooltip, IconButton, Collapse, Modal, Fade } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import formatThaiDate from '../../utils/functions/formatThaiDate';
import ExpensesTable from './ExpensesTable';
import { EXP_STATUS_MAP } from '../../utils/constants/financeStatus.constant';

function InlineStat({ label, value, color = 'text.secondary', tooltip }) {
  const fmt = (n) =>
    (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
  const content = (
    <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.4 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color, lineHeight: 1.4 }} noWrap>
        ฿{fmt(value)}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}

// ─── BudgetTable (header + rows) ─────────────────────────────────────────────
export function BudgetTable({ budgetGroups, budgetKeys, payment }) {
  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      {budgetKeys.map((budgetKey) => (
        <BudgetRow key={budgetKey} expenses={budgetGroups[budgetKey]} payment={payment} />
      ))}
    </Box>
  );
}

// ─── BudgetRow ────────────────────────────────────────────────────────────────
export default function BudgetRow({ expenses, payment }) {
  const [open, setOpen] = useState(false);
  const [expModal, setExpModal] = useState(false);

  const firstExp = expenses[0];
  const budgetId = firstExp?.budget?._id;
  const budgetCodeLabel = firstExp?.budget?.budget_number
    ? `${firstExp.budget.prefix || ''}${firstExp.budget.budget_number}`
    : 'ไม่ระบุงบประมาณ';

  const budgetRows = useSelector((state) => state.budget?.rows || []);
  const budgetData = budgetId
    ? budgetRows.find((b) => String(b._id) === String(budgetId))
    : null;

  const fmt = (n) =>
    (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
  const fmtPct = (n) => `${(typeof n === 'number' ? n : 0).toFixed(2)}%`;

  const bg = budgetData?.cost ?? 0;
  const paid = budgetData?.total_expenses ?? 0;
  const creditorStatus = Array.isArray(budgetData?.totalExpensesByStatus)
    ? budgetData.totalExpensesByStatus.find((s) => s.status === 'APPROVE')
    : null;
  const creditor = creditorStatus?.totalExpenses ?? 0;
  const paidCreditor = paid + creditor;
  const remain = bg > 0 ? bg - paid : 0;
  const pctRemain = bg > 0 ? (remain / bg) * 100 : 0;
  const addBg = remain;
  const pctAddBg = pctRemain;
  const isOver = remain < 0;
  const isWarning = !isOver && pctRemain < 20;
  const hasBudget = bg > 0;

  const statusColor = isOver ? '#dc2626' : isWarning ? '#d97706' : '#16a34a';
  const statusBg = isOver ? '#fef2f2' : isWarning ? '#fffbeb' : '#f0fdf4';
  const statusBgOpen = isOver ? '#fee2e2' : isWarning ? '#fef3c7' : '#dcfce7';
  const borderColor = isOver ? '#fca5a5' : isWarning ? '#fde68a' : '#86efac';
  const remainColor = isOver ? '#dc2626' : isWarning ? '#d97706' : '#16a34a';

  return (
    <Box
      sx={{
        mb: 0.8,
        borderRadius: 1.5,
        border: `1px solid ${borderColor}`,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      {/* Budget header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: 'pointer',
          borderLeft: `3px solid ${statusColor}`,
          backgroundColor: open ? statusBgOpen : statusBg,
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: statusBgOpen },
        }}
      >
        <IconButton size="small" disableRipple sx={{ mr: 1, color: statusColor, flexShrink: 0 }}>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Left: code + name */}
        <Box sx={{ flexShrink: 0, mr: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Chip
              label="งบ"
              size="small"
              sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: '#fde68a', color: '#92400e', borderRadius: 0.5, '& .MuiChip-label': { px: 0.6 } }}
            />
            <Tooltip title="คลิกเพื่อดู expenses ทั้งหมดของงบนี้">
              <Typography
                fontWeight={700}
                fontSize="0.85rem"
                color="#92400e"
                noWrap
                onClick={(e) => { e.stopPropagation(); setExpModal(true); }}
                sx={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
              >
                {budgetCodeLabel}
              </Typography>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {budgetData?.name || '-'} · {expenses.length} รายการ
          </Typography>
        </Box>

        {/* Right: stats */}
        {hasBudget && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              overflowX: 'auto',
              '& > *:not(:last-child)': { borderRight: '1px dashed #e5e7eb' },
            }}
          >
            <InlineStat label="BG" value={bg} color="#1d4ed8" tooltip="งบประมาณที่ตั้ง" />
            <InlineStat label="จ่ายแล้ว" value={paid} color="#374151" tooltip="ค่าใช้จ่ายที่จ่ายแล้ว (SUCCESS)" />
            <InlineStat label="จ่าย+เจ้าหนี้" value={paidCreditor} color="#b45309" tooltip="จ่ายแล้ว + เจ้าหนี้" />
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>%เหลือ</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: remainColor, lineHeight: 1.3 }} noWrap>
                {fmtPct(pctRemain)}
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>เหลือสุทธิ</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: remainColor, lineHeight: 1.3 }} noWrap>
                ฿{fmt(remain)}
              </Typography>
            </Box>
            <InlineStat label="เติม BG" value={addBg} color={isOver ? '#dc2626' : 'text.disabled'} tooltip="งบที่ต้องเติม" />
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>%เติม BG</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: isOver ? '#dc2626' : 'text.disabled', lineHeight: 1.3 }} noWrap>
                {fmtPct(pctAddBg)}
              </Typography>
            </Box>
            <InlineStat label="เจ้าหนี้" value={creditor} color={creditor > 0 ? '#b45309' : 'text.disabled'} tooltip="เจ้าหนี้ (APPROVE ยังไม่จ่าย)" />
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>อัพเดต</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                {budgetData?.updatedAt ? formatThaiDate(budgetData.updatedAt) : '-'}
              </Typography>
            </Box>
            <Tooltip title={budgetData?.description || ''}>
              <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0, maxWidth: 140 }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>หมายเหตุ</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                  {budgetData?.description || '-'}
                </Typography>
              </Box>
            </Tooltip>
            <Box sx={{ px: 1, flexShrink: 0 }}>
              {isOver ? (
                <Chip label="เกินงบ!" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }} />
              ) : isWarning ? (
                <Chip label="ใกล้หมด" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }} />
              ) : (
                <Chip label="ปกติ" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Expand: Expenses table */}
      <Collapse in={open}>
        <Box sx={{ backgroundColor: '#fff' }}>
          <ExpensesTable expenses={expenses} payment={payment} />
        </Box>
      </Collapse>

      {/* ─── Expenses Modal ──────────────────────────────────────────────────── */}
      <Modal open={expModal} onClose={() => setExpModal(false)}>
        <Fade in={expModal}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '88vw', maxWidth: 900, maxHeight: '85vh',
            bgcolor: 'background.paper', boxShadow: 24, borderRadius: 2,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
              <Box>
                <Typography fontWeight={700} fontSize="0.95rem">
                  {budgetCodeLabel} — {budgetData?.name || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  expenses ทั้งหมด {(budgetData?.expenses || []).length} รายการ
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setExpModal(false)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.8, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                {[{ label: 'รหัส', w: 130 }, { label: 'รายละเอียด', w: 220 }, { label: 'วันที่', w: 110 }, { label: 'สถานะ', w: 100, align: 'center' }, { label: 'ยอด (฿)', w: 130, align: 'right' }].map((col) => (
                  <Typography key={col.label} variant="caption" fontWeight={700} color="text.secondary" noWrap
                    sx={{ width: col.w, flexShrink: 0, textAlign: col.align || 'left' }}>
                    {col.label}
                  </Typography>
                ))}
              </Box>

              {(budgetData?.expenses || []).length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">ไม่มีรายการ</Typography>
                </Box>
              ) : (
                _.orderBy(budgetData.expenses, ['dateRequest', 'date', 'createdAt'], ['desc']).map((exp, i) => {
                  const cfg = EXP_STATUS_MAP[exp.status];
                  const dateStr = exp.dateRequest || exp.date || exp.createdAt;
                  return (
                    <Box key={exp._id || i} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.9, borderBottom: '1px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } }}>
                      <Typography variant="body2" fontWeight={700} color="#1d4ed8" noWrap sx={{ width: 130, flexShrink: 0 }}>
                        {exp.code || exp.expenses_code || '-'}
                      </Typography>
                      <Tooltip title={exp.name || exp.detail || '-'} placement="top">
                        <Typography variant="body2" noWrap sx={{ width: 220, flexShrink: 0, color: 'text.secondary' }}>
                          {exp.name || exp.detail || '-'}
                        </Typography>
                      </Tooltip>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ width: 110, flexShrink: 0, fontSize: '0.78rem' }}>
                        {dateStr ? formatThaiDate(dateStr) : '-'}
                      </Typography>
                      <Box sx={{ width: 100, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                        {cfg ? (
                          <Chip label={cfg.label} size="small"
                            sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: cfg.bg, color: cfg.color, '& .MuiChip-label': { px: 0.6 } }} />
                        ) : (
                          <Typography variant="caption" color="text.disabled">{exp.status || '-'}</Typography>
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ width: 130, flexShrink: 0, textAlign: 'right', color: '#1e293b' }}>
                        {(exp.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  );
                })
              )}

              {(budgetData?.expenses || []).length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.2, borderTop: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mr: 1 }}>รวม</Typography>
                  <Typography variant="body2" fontWeight={700} color="#1e293b">
                    {_.sumBy(budgetData.expenses, 'price').toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
